import { displayBonusLabel, type BonusType, type TodayBonusRow } from '../../api/bonuses';

export const DEFAULT_BONUS_COLUMN_ORDER = [
  'experience',
  'aa',
  'coin',
  'loot',
  'rare',
  'tradeskill',
  'respawn',
  'faction',
  'tribute',
  'item-exp',
  'mercenary-exp',
  'none',
  'unconfirmed'
];

export const BONUS_COLUMN_ORDER_STORAGE_KEY = 'fz.resourceHunter.bonusColumnOrder.v1';

export type BonusColumnDefinition = {
  key: string;
  label: string;
  bonusTypeId: string | null;
  isUnconfirmed: boolean;
};

export function levelRange(min: number | null, max: number | null) {
  if (min == null && max == null) {
    return null;
  }

  if (min != null && max != null) {
    return `Lv ${min}-${max}`;
  }

  if (min != null) {
    return `Lv ${min}+`;
  }

  return `Up to ${max}`;
}

export function zoneMeta(row: Pick<TodayBonusRow, 'expansion' | 'minLevel' | 'maxLevel' | 'zoneType'>) {
  return [row.expansion, levelRange(row.minLevel, row.maxLevel), row.zoneType].filter(Boolean).join(' · ');
}

export function buildBonusColumns(types: BonusType[], rows: TodayBonusRow[], columnOrder: string[]): BonusColumnDefinition[] {
  const typeColumns: BonusColumnDefinition[] = types.map((type) => ({
    key: type.key,
    label: displayBonusLabel(type),
    bonusTypeId: type.id,
    isUnconfirmed: type.key === 'unconfirmed'
  }));

  const hasUnconfirmedType = typeColumns.some((type) => type.key === 'unconfirmed');
  const hasUnconfirmedRows = rows.some((row) => row.state === 'UNCONFIRMED' || row.bonus === 'unconfirmed' || !row.bonusTypeId);
  const columns = [...typeColumns];

  if (!hasUnconfirmedType && hasUnconfirmedRows) {
    columns.push({
      key: 'unconfirmed',
      label: 'Unconfirmed',
      bonusTypeId: null,
      isUnconfirmed: true
    });
  }

  const orderIndex = new Map(columnOrder.map((key, index) => [key, index]));

  return columns.sort((a, b) => {
    const aIndex = orderIndex.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderIndex.get(b.key) ?? Number.MAX_SAFE_INTEGER;

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    return a.label.localeCompare(b.label);
  });
}

export function loadColumnOrder(types: BonusType[], rows: TodayBonusRow[]) {
  const knownKeys = new Set([
    ...DEFAULT_BONUS_COLUMN_ORDER,
    ...types.map((type) => type.key),
    ...rows.map((row) => row.bonus).filter(Boolean)
  ]);

  try {
    const raw = window.localStorage.getItem(BONUS_COLUMN_ORDER_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_BONUS_COLUMN_ORDER;
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return DEFAULT_BONUS_COLUMN_ORDER;
    }

    const saved = parsed.filter((key): key is string => typeof key === 'string' && knownKeys.has(key));
    const missing = DEFAULT_BONUS_COLUMN_ORDER.filter((key) => !saved.includes(key));
    const typeMissing = types.map((type) => type.key).filter((key) => !saved.includes(key) && !missing.includes(key));

    return [...saved, ...missing, ...typeMissing];
  } catch {
    return DEFAULT_BONUS_COLUMN_ORDER;
  }
}

export function saveColumnOrder(order: string[]) {
  window.localStorage.setItem(BONUS_COLUMN_ORDER_STORAGE_KEY, JSON.stringify(order));
}


export type ResourceBonusSortKey = 'name' | 'level' | 'expansion' | 'totalBonuses';
export type ResourceBonusSortDirection = 'asc' | 'desc';

export type ResourceBonusGridFilters = {
  expansions: string[];
  zoneType: string;
  minLevel: string;
  maxLevel: string;
};

export type ResourceBonusGridSort = {
  sortBy: ResourceBonusSortKey;
  direction: ResourceBonusSortDirection;
};

export type ResourceBonusFilterableRow = {
  name: string;
  expansion: string | null;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
};

export const DEFAULT_RESOURCE_BONUS_FILTERS: ResourceBonusGridFilters = {
  expansions: [],
  zoneType: '',
  minLevel: '',
  maxLevel: ''
};

export const DEFAULT_RESOURCE_BONUS_SORT: ResourceBonusGridSort = {
  sortBy: 'name',
  direction: 'asc'
};

const EXPANSION_RELEASE_ORDER = [
  'Classic',
  'The Ruins of Kunark',
  'Kunark',
  'The Scars of Velious',
  'Velious',
  'The Shadows of Luclin',
  'Luclin',
  'The Planes of Power',
  'Planes of Power',
  'The Legacy of Ykesha',
  'Legacy of Ykesha',
  'Lost Dungeons of Norrath',
  'Gates of Discord',
  'Omens of War',
  'Dragons of Norrath',
  'Depths of Darkhollow',
  'Prophecy of Ro',
  "The Serpent's Spine",
  'The Buried Sea',
  'Secrets of Faydwer',
  'Seeds of Destruction',
  'Underfoot',
  'House of Thule',
  'Veil of Alaris',
  'Rain of Fear',
  'Call of the Forsaken',
  'The Darkened Sea',
  'The Broken Mirror',
  'Empires of Kunark',
  'Ring of Scale',
  'The Burning Lands',
  'Torment of Velious',
  'Claws of Veeshan',
  'Terror of Luclin',
  'Night of Shadows',
  "Laurion's Song",
  'The Outer Brood'
];

const EXPANSION_ORDER = new Map(EXPANSION_RELEASE_ORDER.map((expansion, index) => [expansion.toLowerCase(), index]));

function normalizedValue(value: string | null | undefined) {
  return value?.trim() ?? '';
}

function expansionSortIndex(expansion: string | null) {
  const normalized = normalizedValue(expansion).toLowerCase();
  return EXPANSION_ORDER.get(normalized) ?? Number.MAX_SAFE_INTEGER;
}

function rowMinLevel(row: Pick<ResourceBonusFilterableRow, 'minLevel'>) {
  return row.minLevel ?? Number.MAX_SAFE_INTEGER;
}

function rowMaxLevel(row: Pick<ResourceBonusFilterableRow, 'maxLevel'>) {
  return row.maxLevel ?? Number.MAX_SAFE_INTEGER;
}

function parseLevelFilter(value: string) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function rowMatchesLevelRange(row: ResourceBonusFilterableRow, minFilter: number | null, maxFilter: number | null) {
  if (minFilter == null && maxFilter == null) {
    return true;
  }

  const rowMin = row.minLevel ?? Number.NEGATIVE_INFINITY;
  const rowMax = row.maxLevel ?? Number.POSITIVE_INFINITY;

  if (minFilter != null && rowMax < minFilter) {
    return false;
  }

  if (maxFilter != null && rowMin > maxFilter) {
    return false;
  }

  return true;
}

export function getResourceBonusFilterOptions(rows: ResourceBonusFilterableRow[]) {
  const expansions = Array.from(new Set(rows.map((row) => normalizedValue(row.expansion)).filter(Boolean))).sort((a, b) => {
    const aIndex = expansionSortIndex(a);
    const bIndex = expansionSortIndex(b);

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    return a.localeCompare(b);
  });

  const zoneTypes = Array.from(new Set(rows.map((row) => normalizedValue(row.zoneType)).filter(Boolean))).sort((a, b) => a.localeCompare(b));

  return { expansions, zoneTypes };
}

export function filterAndSortBonusRows<T extends ResourceBonusFilterableRow>(rows: T[], filters: ResourceBonusGridFilters, sort: ResourceBonusGridSort) {
  const minFilter = parseLevelFilter(filters.minLevel);
  const maxFilter = parseLevelFilter(filters.maxLevel);
  const expansionFilters = filters.expansions.map((value) => value.trim()).filter(Boolean);
  const zoneTypeFilter = filters.zoneType.trim();

  const filtered = rows.filter((row) => {
    if (expansionFilters.length > 0 && !expansionFilters.includes(normalizedValue(row.expansion))) {
      return false;
    }

    if (zoneTypeFilter && row.zoneType !== zoneTypeFilter) {
      return false;
    }

    return rowMatchesLevelRange(row, minFilter, maxFilter);
  });

  const directionMultiplier = sort.direction === 'desc' ? -1 : 1;

  return [...filtered].sort((a, b) => {
    let comparison = 0;

    if (sort.sortBy === 'level') {
      comparison = rowMinLevel(a) - rowMinLevel(b) || rowMaxLevel(a) - rowMaxLevel(b);
    } else if (sort.sortBy === 'expansion') {
      comparison = expansionSortIndex(a.expansion) - expansionSortIndex(b.expansion) || normalizedValue(a.expansion).localeCompare(normalizedValue(b.expansion));
    } else {
      comparison = a.name.localeCompare(b.name);
    }

    if (comparison === 0) {
      comparison = a.name.localeCompare(b.name);
    }

    return comparison * directionMultiplier;
  });
}

export function hasActiveResourceBonusFilters(filters: ResourceBonusGridFilters) {
  return Boolean(filters.expansions.length || filters.zoneType || filters.minLevel || filters.maxLevel);
}

export function groupRowsByBonus(rows: TodayBonusRow[]) {
  const grouped = new Map<string, TodayBonusRow[]>();

  for (const row of rows) {
    const key = row.state === 'UNCONFIRMED' || !row.bonusTypeId ? 'unconfirmed' : row.bonus || 'unconfirmed';
    grouped.set(key, [...(grouped.get(key) ?? []), row]);
  }

  return grouped;
}

export function isUnconfirmedAssignment(row: TodayBonusRow | null | undefined) {
  return Boolean(row && (row.state === 'UNCONFIRMED' || row.bonus === 'unconfirmed' || !row.bonusTypeId));
}

export function findExistingZoneAssignment(rows: TodayBonusRow[], zoneId: string) {
  return rows.find((row) => row.zoneId === zoneId && row.state !== 'REMOVED') ?? null;
}
